import { useState, useEffect } from 'react';
import { useRole } from '@/contexts/RoleContext';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CleanerDashboard } from '@/components/cleaner/CleanerDashboard';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/integrations/supabase/types';

const roleLabels: Record<string, string> = {
  owner: 'Właściciel',
  coordinator: 'Koordynator',
  cleaner: 'Sprzątacz',
};

const roleBadgeStyles: Record<string, string> = {
  owner: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
  coordinator: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  cleaner: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
};

type CleaningTask = Database['public']['Tables']['cleaning_tasks']['Row'];

const Index = () => {
  const { userName, availableRoles, isLoading, currentRole, currentOrgId } = useRole();
  const [tasks, setTasks] = useState<CleaningTask[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(true);

  useEffect(() => {
    const fetchTasks = async () => {
      // Wait for role context to load and ensure currentOrgId is available
      if (isLoading || !currentOrgId) {
        return;
      }

      try {
        console.log('[Index] Pobieranie zadań dla org_id:', currentOrgId);
        
        // Get current user
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        
        if (authError || !user) {
          console.error('[Index] Błąd autentykacji:', authError);
          setIsLoadingTasks(false);
          return;
        }

        // Fetch 3 tasks assigned to current user with org_id filter
        const { data: tasksData, error: tasksError } = await supabase
          .from('cleaning_tasks')
          .select('*')
          .eq('org_id', currentOrgId)
          .eq('assigned_staff_id', user.id)
          .order('scheduled_at', { ascending: true })
          .limit(3);
        
        console.log('[Index] Pobrano zadań:', tasksData?.length || 0, 'dla org_id:', currentOrgId);

        if (tasksError) {
          console.error('[Index] Błąd pobierania zadań:', tasksError);
        } else {
          setTasks(tasksData || []);
        }
      } catch (err) {
        console.error('[Index] Nieoczekiwany błąd:', err);
      } finally {
        setIsLoadingTasks(false);
      }
    };

    fetchTasks();
  }, [isLoading, currentOrgId]);

  // Render cleaner dashboard when role is cleaner
  if (currentRole === 'cleaner') {
    return <CleanerDashboard />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        {isLoading ? (
          <div className="space-y-4 text-center">
            <Skeleton className="h-8 w-64 mx-auto" />
            <Skeleton className="h-6 w-48 mx-auto" />
            <div className="flex gap-2 justify-center">
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-6 w-20" />
            </div>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-bold mb-2">
              Witaj, {userName || 'Użytkowniku'}!
            </h1>
            
            {availableRoles.length > 0 && (
              <div className="mt-6 text-center">
                <p className="text-muted-foreground mb-3">Twoje role to:</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {availableRoles.map((role) => (
                    <Badge 
                      key={role} 
                      variant="secondary"
                      className={roleBadgeStyles[role] || ''}
                    >
                      {roleLabels[role] || role}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {currentRole && (
              <p className="mt-6 text-sm text-muted-foreground">
                Aktualnie przeglądasz jako:{' '}
                <span className="font-medium text-foreground">
                  {roleLabels[currentRole] || currentRole}
                </span>
              </p>
            )}

            {/* Tasks Section */}
            <div className="mt-8 w-full max-w-2xl">
              <h2 className="text-xl font-semibold mb-4">Twoje zadania</h2>
              {isLoadingTasks ? (
                <div className="space-y-3">
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              ) : tasks.length > 0 ? (
                <div className="space-y-3">
                  {tasks.map((task) => (
                    <Card key={task.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium">
                              Zadanie #{task.id.slice(0, 8)}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              Status: {task.status || 'Brak statusu'}
                              {task.priority && ` • Priorytet: ${task.priority}`}
                            </p>
                            {task.scheduled_at && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Zaplanowano: {new Date(task.scheduled_at).toLocaleDateString('pl-PL')}
                              </p>
                            )}
                          </div>
                          {task.status && (
                            <Badge variant="secondary">
                              {task.status}
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground text-center">
                      Brak przypisanych zadań
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}
      </div>
      
      {/* Footer */}
      <footer className="border-t border-border bg-card py-4 mt-auto">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          © 2026 DOMIO System. Wszelkie prawa zastrzeżone.
        </div>
      </footer>
    </div>
  );
};

export default Index;
